import { useEffect, useState } from "react";
import { type Agreement, ApiError, type OnboardingState, api } from "../lib/api";
import { Notice, SandboxPanel } from "../components/Shell";

export function AgreementsStep({ session, onUpdate }: { session: string | null; onUpdate: (s: OnboardingState) => void }) {
  const [docs, setDocs] = useState<Agreement[] | null>(null);
  const [read, setRead] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [esign, setEsign] = useState<{ sessionId: string } | null>(null);

  useEffect(() => {
    api<Agreement[]>("/v1/onboarding/agreements").then(setDocs, () => setError("Could not load the documents."));
  }, []);

  async function run(fn: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong.");
    } finally {
      setBusy(false);
    }
  }

  // Returning from the live eSign provider.
  useEffect(() => {
    if (session && !session.startsWith("sbx_")) {
      void run(async () => onUpdate(await api<OnboardingState>("/v1/onboarding/agreements/esign/complete", { body: { sessionId: session } })));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  const start = () =>
    run(async () => {
      const s = await api<{ sessionId: string; redirectUrl: string }>("/v1/onboarding/agreements/esign/start", { method: "POST" });
      if (s.redirectUrl.includes("sandbox=")) setEsign(s);
      else window.location.href = s.redirectUrl;
    });

  const finishSandbox = (outcome: "approve" | "fail") =>
    run(async () => {
      await api(`/v1/sandbox/sessions/${esign!.sessionId}/${outcome}`, { method: "POST" });
      try {
        onUpdate(await api<OnboardingState>("/v1/onboarding/agreements/esign/complete", { body: { sessionId: esign!.sessionId } }));
      } finally {
        setEsign(null);
      }
    });

  return (
    <div>
      <h1>Read and sign</h1>
      <p className="muted">These set out how Finversify's research service works, what it costs and the risks involved. You sign all of them once with Aadhaar eSign.</p>
      {error && <Notice tone="bad">{error}</Notice>}
      {docs?.map((d) => (
        <details className="doc" key={d.type}>
          <summary>
            {d.title}
            <span className={`tag ${d.signed ? "good" : ""}`}>{d.signed ? "Signed" : `Version ${d.version}`}</span>
          </summary>
          <div className="body">{d.body}</div>
        </details>
      ))}
      <label className="check" style={{ marginTop: 16 }}>
        <input type="checkbox" checked={read} onChange={(e) => setRead(e.target.checked)} />
        <span>I have read these documents and agree to them.</span>
      </label>
      <button className="btn" onClick={() => void start()} disabled={busy || !read || !docs}>
        Sign with Aadhaar eSign
      </button>
      {esign && (
        <SandboxPanel title="Aadhaar eSign">
          <p>Decide how this test signing ends.</p>
          <div className="row">
            <button className="btn" onClick={() => void finishSandbox("approve")} disabled={busy}>
              Sign documents
            </button>
            <button className="btn btn-quiet" onClick={() => void finishSandbox("fail")} disabled={busy}>
              Cancel signing
            </button>
          </div>
        </SandboxPanel>
      )}
    </div>
  );
}
